import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Globe, Plus, Trash2, RefreshCw, Activity, ShieldCheck, Lock, Radar,
  Play, Pause, History, ArrowUpRight, AlertTriangle, LayoutGrid, Rows3,
  ArrowUpDown, ArrowUp, ArrowDown, Download, BellRing, ScanSearch
} from 'lucide-react';
import { exportToCSV } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  http: 'HTTP Health Check', ping: 'PING (ICMP)', tcp: 'TCP Port',
  status: 'Status de página', redirect: 'Redirecionamento HTTP→HTTPS',
};

const statusDot = (status) => {
  const color = status === 'up' ? 'bg-green-500' : status === 'down' ? 'bg-red-500' : 'bg-zinc-400';
  return <span className={`inline-block h-2.5 w-2.5 rounded-full ${color}`} />;
};

const fmtUptime = (v) => {
  if (v === null || v === undefined) return <span className="text-muted-foreground">—</span>;
  const cls = v >= 99.5 ? 'text-green-600' : v >= 95 ? 'text-amber-500' : 'text-red-500';
  return <span className={cls}>{v.toFixed(2)}%</span>;
};

const SortHeader = ({ label, sortKey, sort, onSort, align = 'left' }) => {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <TableHead className={align === 'center' ? 'text-center' : ''}>
      <button
        onClick={() => onSort(sortKey)}
        data-testid={`sort-${sortKey}`}
        className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${active ? 'text-foreground font-semibold' : ''}`}
      >
        {label}
        <Icon className={`h-3 w-3 ${active ? 'opacity-100' : 'opacity-40'}`} />
      </button>
    </TableHead>
  );
};

export function WebMonitorPage() {
  const [data, setData] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgFilter, setOrgFilter] = useState('all');
  const [monitorDialog, setMonitorDialog] = useState(false);
  const [domainDialog, setDomainDialog] = useState(false);
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState(null);
  const [mForm, setMForm] = useState({
    name: '', type: 'http', target: '', org_id: '', method: 'GET',
    expected_status: 200, expected_text: '', port: 443, interval_seconds: 300,
  });
  const [dForm, setDForm] = useState({ domain: '', org_id: '' });
  const [domainView, setDomainView] = useState(() => localStorage.getItem('oneview-domain-view') || 'grid');
  const [sort, setSort] = useState({ key: 'domain', dir: 'asc' });
  const navigate = useNavigate();
  const { getAuthHeader } = useAuthStore();

  const changeDomainView = (view) => {
    setDomainView(view);
    localStorage.setItem('oneview-domain-view', view);
  };

  const toggleSort = (key) => {
    setSort(s => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const sortDomains = (list) => {
    const value = (d) => {
      const i = d.last_intel || {};
      switch (sort.key) {
        case 'org': return (d.org_name || '').toLowerCase();
        case 'issuer': return (i.ssl_issuer || '').toLowerCase();
        case 'ssl': return d.ssl_days_left ?? 99999;
        case 'expires': return d.domain_days_left ?? 99999;
        case 'registrar': return (i.domain_registrar || i.domain_registrant || '').toLowerCase();
        case 'waf': return (d.waf_cdn || []).join(',').toLowerCase();
        case 'subs': return i.subdomains_total ?? -1;
        default: return (d.domain || '').toLowerCase();
      }
    };
    return [...list].sort((a, b) => {
      const va = value(a), vb = value(b);
      const unknownA = va === 99999 || va === -1 || va === '';
      const unknownB = vb === 99999 || vb === -1 || vb === '';
      if (unknownA !== unknownB) return unknownA ? 1 : -1;
      if (va === vb) return 0;
      return (va > vb ? 1 : -1) * (sort.dir === 'asc' ? 1 : -1);
    });
  };

  const exportDomains = (list) => {
    if (list.length === 0) { toast.error('Nenhum domínio para exportar'); return; }
    const rows = list.map(d => {
      const i = d.last_intel || {};
      return {
        domain: d.domain,
        org: d.org_name,
        ssl_issuer: i.ssl_issuer || '',
        ssl_valid_until: i.ssl_valid_until || '',
        ssl_days_left: d.ssl_days_left ?? '',
        domain_expires: i.domain_expires || '',
        domain_days_left: d.domain_days_left ?? '',
        registrar: i.domain_registrar || '',
        registrant: i.domain_registrant || '',
        nameservers: (i.domain_nameservers || []).join(' | '),
        waf: (d.waf_cdn || []).join(' | '),
        subdomains: i.subdomains_total ?? '',
        dns_a: ((i.dns || {}).A || []).join(' | '),
      };
    });
    exportToCSV(rows, [
      { key: 'domain', label: 'Domínio' },
      { key: 'org', label: 'Cliente/Org' },
      { key: 'ssl_issuer', label: 'Emissor SSL' },
      { key: 'ssl_valid_until', label: 'SSL validade' },
      { key: 'ssl_days_left', label: 'SSL dias restantes' },
      { key: 'domain_expires', label: 'Domínio expira' },
      { key: 'domain_days_left', label: 'Domínio dias restantes' },
      { key: 'registrar', label: 'Registrar' },
      { key: 'registrant', label: 'Titular' },
      { key: 'nameservers', label: 'Nameservers' },
      { key: 'waf', label: 'WAF/CDN' },
      { key: 'subdomains', label: 'Subdomínios' },
      { key: 'dns_a', label: 'Registros A' },
    ], `dominios_oneview_${new Date().toISOString().slice(0, 10)}`);
    toast.success(`${list.length} domínio(s) exportado(s)`);
  };

  const testAlert = async (d) => {
    try {
      const res = await axios.post(`${API}/webmonitor/domains/${d.id}/test-alert`, {},
        { headers: getAuthHeader() });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar alerta de teste');
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [res, orgsRes] = await Promise.all([
        axios.get(`${API}/webmonitor/overview`, { headers: getAuthHeader() }),
        axios.get(`${API}/orgs`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
      ]);
      setData(res.data);
      setOrgs(orgsRes.data);
    } catch (err) {
      console.warn('Erro ao carregar Web Monitor:', err);
      toast.error('Erro ao carregar Web Monitor');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createMonitor = async () => {
    if (!mForm.name || !mForm.target) { toast.error('Informe nome e destino'); return; }
    try {
      await axios.post(`${API}/webmonitor/monitors`, { ...mForm, org_id: mForm.org_id || null },
        { headers: getAuthHeader() });
      toast.success('Monitor criado — primeira verificação em andamento');
      setMonitorDialog(false);
      setMForm({ name: '', type: 'http', target: '', org_id: '', method: 'GET', expected_status: 200, expected_text: '', port: 443, interval_seconds: 300 });
      setTimeout(fetchData, 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar monitor');
    }
  };

  const createDomain = async () => {
    if (!dForm.domain) { toast.error('Informe o domínio'); return; }
    try {
      const res = await axios.post(`${API}/webmonitor/domains`, { ...dForm, org_id: dForm.org_id || null },
        { headers: getAuthHeader() });
      const created = res.data.monitors_created || [];
      toast.success(created.length
        ? `Domínio adicionado — ${created.length} monitor(es) criado(s) automaticamente`
        : 'Domínio adicionado — coletando SSL, registro e WAF');
      setDomainDialog(false);
      setDForm({ domain: '', org_id: '' });
      setTimeout(fetchData, 4000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao adicionar domínio');
    }
  };

  const runNow = async (m) => {
    toast.info('Executando verificação...');
    try {
      const res = await axios.post(`${API}/webmonitor/monitors/${m.id}/run`, {}, { headers: getAuthHeader() });
      toast.success(`${m.name}: ${res.data.status === 'up' ? 'OK' : 'Falha'} — ${res.data.message}`);
      fetchData();
    } catch (_) { toast.error('Erro ao executar verificação'); }
  };

  const togglePause = async (m) => {
    const action = m.paused ? 'resume' : 'pause';
    try {
      const res = await axios.post(`${API}/webmonitor/monitors/${m.id}/${action}`, {},
        { headers: getAuthHeader() });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar o monitor');
    }
  };

  const enableAlerts = async (m) => {
    try {
      const res = await axios.post(`${API}/webmonitor/monitors/${m.id}/enable-alerts`, {},
        { headers: getAuthHeader() });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao ativar alertas');
    }
  };

  const deleteMonitor = async (m) => {
    if (!window.confirm(`Excluir o monitor ${m.name}?`)) return;
    await axios.delete(`${API}/webmonitor/monitors/${m.id}`, { headers: getAuthHeader() });
    toast.success('Monitor removido');
    fetchData();
  };

  const openDomain = async (d) => {
    try {
      const res = await axios.get(`${API}/webmonitor/domains/${d.id}`, { headers: getAuthHeader() });
      setDetail(res.data);
    } catch (_) { toast.error('Erro ao carregar domínio'); }
  };

  const refreshDomain = async (d) => {
    toast.info('Atualizando inteligência do domínio...');
    try {
      await axios.post(`${API}/webmonitor/domains/${d.id}/refresh`, {}, { headers: getAuthHeader() });
      toast.success('Dados atualizados');
      openDomain(d);
      fetchData();
    } catch (_) { toast.error('Erro ao atualizar'); }
  };

  const deleteDomain = async (d) => {
    if (!window.confirm(`Remover o domínio ${d.domain}?`)) return;
    await axios.delete(`${API}/webmonitor/domains/${d.id}`, { headers: getAuthHeader() });
    toast.success('Domínio removido');
    setDetail(null);
    fetchData();
  };

  const promote = async (sub) => {
    await axios.post(`${API}/webmonitor/domains/${detail.id}/promote`, { subdomain: sub },
      { headers: getAuthHeader() });
    toast.success(`${sub} promovido a monitor ativo`);
    fetchData();
  };

  const openHistory = async (m) => {
    try {
      const res = await axios.get(`${API}/webmonitor/monitors/${m.id}/history`, { headers: getAuthHeader() });
      setHistory(res.data);
    } catch (_) { toast.error('Erro ao carregar histórico'); }
  };

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-80 w-full" /></div>;
  }

  const monitors = orgFilter === 'all' ? data.monitors : data.monitors.filter(m => m.org_id === orgFilter);
  const domains = orgFilter === 'all' ? data.domains : data.domains.filter(d => d.org_id === orgFilter);
  const intel = detail?.last_intel || {};

  return (
    <div className="space-y-6" data-testid="webmonitor-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Radar className="h-5 w-5" /> Web Monitor
          </h2>
          <p className="text-sm text-muted-foreground">
            Checks sintéticos, SSL, registro de domínio, WAF/CDN e descoberta de subdomínios
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={orgFilter} onValueChange={setOrgFilter}>
            <SelectTrigger className="w-[210px]" data-testid="webmonitor-org-filter">
              <SelectValue placeholder="Cliente/Org" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os clientes</SelectItem>
              {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.display_name || o.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setDomainDialog(true)} data-testid="add-domain-btn">
            <Globe className="h-4 w-4 mr-2" /> Adicionar Domínio
          </Button>
          <Button onClick={() => setMonitorDialog(true)} data-testid="add-monitor-btn">
            <Plus className="h-4 w-4 mr-2" /> Adicionar Monitor
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-5">
        {[
          { label: 'Monitores', value: data.summary.total, icon: Activity },
          { label: 'Online', value: data.summary.up, icon: ShieldCheck, color: 'text-green-500' },
          { label: 'Com falha', value: data.summary.down, icon: AlertTriangle, color: 'text-red-500' },
          { label: 'Domínios', value: data.summary.domains, icon: Globe },
          { label: 'Vencimentos ≤30d', value: data.alerts.length, icon: Lock, color: 'text-amber-500' },
        ].map(card => (
          <Card key={card.label}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <div className={`text-2xl font-bold ${card.color || ''}`}>{card.value}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mt-1">{card.label}</div>
              </div>
              <card.icon className={`h-5 w-5 ${card.color || 'text-muted-foreground'}`} />
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="monitors">
        <TabsList>
          <TabsTrigger value="monitors" data-testid="tab-monitors">Monitores ({monitors.length})</TabsTrigger>
          <TabsTrigger value="domains" data-testid="tab-domains">Domínios ({domains.length})</TabsTrigger>
          <TabsTrigger value="incidents" data-testid="tab-web-incidents">
            Incidentes {data.open_incidents.length > 0 && <Badge variant="destructive" className="ml-2">{data.open_incidents.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="monitors" className="mt-4">
          <Card><CardContent className="p-0">
            {monitors.length === 0 ? (
              <div className="p-12 text-center">
                <Activity className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                <h3 className="font-medium">Nenhum monitor</h3>
                <p className="text-sm text-muted-foreground">Crie checks HTTP, PING, TCP ou de status de página</p>
              </div>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Monitor</TableHead><TableHead>Cliente</TableHead>
                  <TableHead>Destino</TableHead><TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">24h</TableHead>
                  <TableHead className="text-center">7d</TableHead>
                  <TableHead className="text-center">30d</TableHead>
                  <TableHead className="text-center">Resposta</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {monitors.map(m => (
                    <TableRow key={m.id} data-testid={`monitor-row-${m.id}`}>
                      <TableCell>
                        <button className="font-medium hover:underline text-left"
                          onClick={() => navigate(`/dashboard/webmonitor/monitors/${m.id}`)}
                          data-testid={`open-monitor-${m.id}`}>{m.name}</button>
                        <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                          <span>{TYPE_LABELS[m.type] || m.type}</span>
                          {m.paused && <Badge variant="outline" className="text-[10px]">pausado</Badge>}
                          {m.alerting === false && (
                            <Badge variant="outline" className="text-[10px]" title={m.mode_reason || ''}>
                              informativo
                            </Badge>
                          )}
                          {m.alerting === false && (
                            <button onClick={() => enableAlerts(m)} data-testid={`enable-alerts-${m.id}`}
                              className="text-[10px] underline hover:text-foreground">ativar alertas</button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{m.org_name}</TableCell>
                      <TableCell className="text-sm font-mono truncate max-w-[200px]">{m.target}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          {statusDot(m.paused ? 'paused' : m.last_status)}
                          <span className="text-xs text-muted-foreground truncate max-w-[160px]">
                            {m.last_message || 'aguardando'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center text-sm">{fmtUptime(m.uptime_24h)}</TableCell>
                      <TableCell className="text-center text-sm">{fmtUptime(m.uptime_7d)}</TableCell>
                      <TableCell className="text-center text-sm">{fmtUptime(m.uptime_30d)}</TableCell>
                      <TableCell className="text-center text-sm">{m.last_latency_ms ? `${m.last_latency_ms} ms` : '—'}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => togglePause(m)}
                          title={m.paused ? 'Retomar' : 'Pausar'} data-testid={`pause-monitor-${m.id}`}>
                          {m.paused ? <Play className="h-4 w-4 text-emerald-600" /> : <Pause className="h-4 w-4" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => runNow(m)} title="Verificar agora"
                          data-testid={`run-monitor-${m.id}`}><RefreshCw className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => openHistory(m)} title="Histórico"
                          data-testid={`history-monitor-${m.id}`}><History className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteMonitor(m)}
                          data-testid={`delete-monitor-${m.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="domains" className="mt-4 space-y-3">
          {data.alerts.length > 0 && (
            <Card className="border-amber-500/40 bg-amber-500/5" data-testid="expiry-alerts-panel">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <BellRing className="h-4 w-4 text-amber-500" />
                  <span className="text-sm font-medium">Vencimentos nos próximos 30 dias</span>
                  <Badge variant="outline" className="ml-1">{data.alerts.length}</Badge>
                  <span className="text-xs text-muted-foreground ml-auto">
                    Avisos automáticos por e-mail em 30, 15 e 7 dias
                  </span>
                </div>
                <div className="grid gap-1.5 md:grid-cols-2">
                  {data.alerts.map(a => (
                    <div key={`${a.domain_id}-${a.kind}`} className="flex items-center justify-between text-sm">
                      <span className="truncate">
                        <span className="font-medium">{a.domain}</span>
                        <span className="text-muted-foreground"> · {a.kind}</span>
                      </span>
                      <span className="flex items-center gap-2">
                        <span className={a.days <= 7 ? 'text-red-500 font-medium' : 'text-amber-500'}>
                          {a.days} dias
                        </span>
                        {(a.notified || []).length > 0 && (
                          <Badge variant="secondary" className="text-[10px]">
                            avisado em {(a.notified || []).join('/')}d
                          </Badge>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm text-muted-foreground">
              {domains.length} domínio(s) — clique no cabeçalho para ordenar
            </p>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => exportDomains(sortDomains(domains))}
                data-testid="export-domains-btn">
                <Download className="h-3.5 w-3.5 mr-1.5" /> Exportar CSV
              </Button>
              <div className="flex items-center gap-1 border rounded-md p-0.5">
                <Button size="sm" variant={domainView === 'grid' ? 'secondary' : 'ghost'}
                  className="h-7 px-2" onClick={() => changeDomainView('grid')} data-testid="domain-view-grid">
                  <Rows3 className="h-3.5 w-3.5 mr-1.5" /> Grid
                </Button>
                <Button size="sm" variant={domainView === 'cards' ? 'secondary' : 'ghost'}
                  className="h-7 px-2" onClick={() => changeDomainView('cards')} data-testid="domain-view-cards">
                  <LayoutGrid className="h-3.5 w-3.5 mr-1.5" /> Cards
                </Button>
              </div>
            </div>
          </div>

          {domains.length === 0 ? (
            <Card><CardContent className="p-12 text-center">
              <Globe className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <h3 className="font-medium">Nenhum domínio</h3>
              <p className="text-sm text-muted-foreground">Cadastre domínios no Cliente/Org ou adicione aqui</p>
            </CardContent></Card>
          ) : domainView === 'grid' ? (
            <Card><CardContent className="p-0">
              <Table data-testid="domains-grid">
                <TableHeader><TableRow>
                  <SortHeader label="Domínio" sortKey="domain" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Cliente/Org" sortKey="org" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Emissor SSL" sortKey="issuer" sort={sort} onSort={toggleSort} />
                  <SortHeader label="SSL expira" sortKey="ssl" sort={sort} onSort={toggleSort} align="center" />
                  <SortHeader label="Domínio expira" sortKey="expires" sort={sort} onSort={toggleSort} align="center" />
                  <SortHeader label="Registrar" sortKey="registrar" sort={sort} onSort={toggleSort} />
                  <SortHeader label="WAF / CDN" sortKey="waf" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Subdomínios" sortKey="subs" sort={sort} onSort={toggleSort} align="center" />
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {sortDomains(domains).map(d => {
                    const di = d.last_intel || {};
                    return (
                      <TableRow key={d.id} data-testid={`domain-row-${d.id}`}>
                        <TableCell>
                          <button className="font-medium hover:underline flex items-center gap-2"
                            onClick={() => openDomain(d)}>
                            <Globe className="h-3.5 w-3.5 text-primary" /> {d.domain}
                          </button>
                          <div className="text-[10px] text-muted-foreground mt-0.5">
                            {d.collecting ? (
                              <span className="text-primary">coletando agora...</span>
                            ) : d.last_collect ? (
                              `coleta ${new Date(d.last_collect).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                            ) : 'aguardando 1ª coleta'}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{d.org_name}</TableCell>
                        <TableCell className="text-sm">{di.ssl_issuer || '—'}</TableCell>
                        <TableCell className="text-center text-sm">
                          {d.ssl_days_left !== null && d.ssl_days_left !== undefined ? (
                            <span className={d.ssl_days_left <= 30 ? 'text-amber-500 font-medium' : ''}>
                              {d.ssl_days_left} dias
                            </span>
                          ) : di.ssl_error ? (
                            <span className="text-destructive text-xs" title={di.ssl_error}>indisponível</span>
                          ) : <span className="text-muted-foreground">coletando...</span>}
                          {di.ssl_valid_until && (
                            <div className="text-xs text-muted-foreground">{di.ssl_valid_until}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-center text-sm">
                          {d.domain_days_left ? (
                            <span className={d.domain_days_left <= 30 ? 'text-amber-500 font-medium' : ''}>
                              {d.domain_days_left} dias
                            </span>
                          ) : <span className="text-muted-foreground">não disponível</span>}
                          {di.domain_expires && di.domain_expires !== 'não disponível' && (
                            <div className="text-xs text-muted-foreground">{di.domain_expires}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm truncate max-w-[170px]">
                          {di.domain_registrar && di.domain_registrar !== 'não disponível'
                            ? di.domain_registrar
                            : (di.domain_registrant && di.domain_registrant !== 'não disponível'
                              ? `${di.domain_registrant} (titular)` : '—')}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(d.waf_cdn || []).map(w => (
                              <Badge key={w} variant="secondary" className="text-xs">{w}</Badge>
                            ))}
                            {(d.waf_cdn || []).length === 0 && <span className="text-sm text-muted-foreground">—</span>}
                          </div>
                        </TableCell>
                        <TableCell className="text-center text-sm">{di.subdomains_total ?? '—'}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" title="Detalhes (dossiê completo)"
                            onClick={() => navigate(`/dashboard/webmonitor/domains/${d.id}`)}
                            data-testid={`dossier-${d.id}`}><ScanSearch className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" title="Atualizar dados" onClick={() => refreshDomain(d)}
                            data-testid={`refresh-domain-row-${d.id}`}><RefreshCw className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" title="Enviar alerta de teste" onClick={() => testAlert(d)}
                            data-testid={`test-alert-${d.id}`}><BellRing className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => deleteDomain(d)}
                            data-testid={`delete-domain-${d.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent></Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="domains-cards">
              {sortDomains(domains).map(d => (
                <Card key={d.id} className="cursor-pointer hover:border-primary/60 transition-colors"
                  onClick={() => openDomain(d)} data-testid={`domain-card-${d.id}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Globe className="h-4 w-4 text-primary" /> {d.domain}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">{d.org_name}</p>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">SSL expira em</span>
                      <span className={d.ssl_days_left !== null && d.ssl_days_left <= 30 ? 'text-amber-500 font-medium' : ''}>
                        {d.ssl_days_left !== null && d.ssl_days_left !== undefined ? `${d.ssl_days_left} dias` : 'coletando...'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Domínio expira em</span>
                      <span>{d.domain_days_left ? `${d.domain_days_left} dias` : 'não disponível'}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 pt-1">
                      {(d.waf_cdn || []).map(w => <Badge key={w} variant="secondary" className="text-xs">{w}</Badge>)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="incidents" className="mt-4">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Monitor</TableHead><TableHead>Destino</TableHead><TableHead>Início</TableHead>
                <TableHead>Mensagem</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.open_incidents.map(i => (
                  <TableRow key={i.id}>
                    <TableCell className="font-medium">{i.monitor_name}</TableCell>
                    <TableCell className="font-mono text-sm">{i.target}</TableCell>
                    <TableCell className="text-sm">{new Date(i.started_at).toLocaleString('pt-BR')}</TableCell>
                    <TableCell className="text-sm">{i.message}</TableCell>
                    <TableCell><Badge variant="destructive">Em aberto</Badge></TableCell>
                  </TableRow>
                ))}
                {data.open_incidents.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-sm text-muted-foreground">
                    Nenhum incidente de Web Monitor em aberto
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* New monitor */}
      <Dialog open={monitorDialog} onOpenChange={setMonitorDialog}>
        <DialogContent className="sm:max-w-md flex flex-col max-h-[88vh]">
          <DialogHeader><DialogTitle>Adicionar Monitor</DialogTitle>
            <DialogDescription>Checks independentes do Zabbix, executados pelo OneView</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 pr-1">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input value={mForm.name} onChange={e => setMForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Portal do cliente" data-testid="monitor-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={mForm.type} onValueChange={v => setMForm(f => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="monitor-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{mForm.type === 'http' || mForm.type === 'status' ? 'URL *' : 'Host/IP *'}</Label>
              <Input value={mForm.target} onChange={e => setMForm(f => ({ ...f, target: e.target.value }))}
                placeholder={mForm.type === 'http' ? 'https://app.cliente.com.br' : 'app.cliente.com.br'}
                data-testid="monitor-target-input" />
            </div>
            <div className="space-y-2">
              <Label>Cliente/Org</Label>
              <Select value={mForm.org_id} onValueChange={v => setMForm(f => ({ ...f, org_id: v }))}>
                <SelectTrigger data-testid="monitor-org-select"><SelectValue placeholder="Opcional" /></SelectTrigger>
                <SelectContent>
                  {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.display_name || o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(mForm.type === 'http' || mForm.type === 'status') && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Método</Label>
                    <Select value={mForm.method} onValueChange={v => setMForm(f => ({ ...f, method: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['GET', 'POST', 'HEAD'].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Código esperado</Label>
                    <Input type="number" value={mForm.expected_status}
                      onChange={e => setMForm(f => ({ ...f, expected_status: Number(e.target.value) }))} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Texto esperado (opcional)</Label>
                  <Input value={mForm.expected_text}
                    onChange={e => setMForm(f => ({ ...f, expected_text: e.target.value }))} />
                </div>
              </>
            )}
            {mForm.type === 'tcp' && (
              <div className="space-y-2">
                <Label>Porta</Label>
                <Input type="number" value={mForm.port}
                  onChange={e => setMForm(f => ({ ...f, port: Number(e.target.value) }))} />
              </div>
            )}
            <div className="space-y-2">
              <Label>Intervalo (segundos)</Label>
              <Select value={String(mForm.interval_seconds)}
                onValueChange={v => setMForm(f => ({ ...f, interval_seconds: Number(v) }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[60, 180, 300, 600, 1800].map(s => <SelectItem key={s} value={String(s)}>{s / 60} min</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMonitorDialog(false)}>Cancelar</Button>
            <Button onClick={createMonitor} data-testid="save-monitor-btn">Criar Monitor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New domain */}
      <Dialog open={domainDialog} onOpenChange={setDomainDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Adicionar Domínio</DialogTitle>
            <DialogDescription>Coleta passiva: SSL, registrar, WAF/CDN e subdomínios públicos</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Domínio *</Label>
              <Input value={dForm.domain} onChange={e => setDForm(f => ({ ...f, domain: e.target.value }))}
                placeholder="cliente.com.br" data-testid="domain-input" />
            </div>
            <div className="space-y-2">
              <Label>Cliente/Org</Label>
              <Select value={dForm.org_id} onValueChange={v => setDForm(f => ({ ...f, org_id: v }))}>
                <SelectTrigger data-testid="domain-org-select"><SelectValue placeholder="Opcional" /></SelectTrigger>
                <SelectContent>
                  {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.display_name || o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDomainDialog(false)}>Cancelar</Button>
            <Button onClick={createDomain} data-testid="save-domain-btn">Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Domain detail */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="sm:max-w-3xl flex flex-col max-h-[88vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" /> {detail?.domain}
            </DialogTitle>
            <DialogDescription>Inteligência de domínio e ativos descobertos</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="overflow-y-auto space-y-4 flex-1 pr-1">
              <div className="flex gap-2">
                <Button size="sm" onClick={() => navigate(`/dashboard/webmonitor/domains/${detail.id}`)}
                  data-testid="open-dossier-from-modal">
                  <ScanSearch className="h-4 w-4 mr-2" /> Abrir dossiê completo
                </Button>
                <Button size="sm" variant="outline" onClick={() => refreshDomain(detail)} data-testid="refresh-domain-btn">
                  <RefreshCw className="h-4 w-4 mr-2" /> Atualizar dados
                </Button>
                <Button size="sm" variant="outline" onClick={() => deleteDomain(detail)}>
                  <Trash2 className="h-4 w-4 mr-2 text-destructive" /> Remover
                </Button>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2">
                  <Lock className="h-4 w-4" /> Certificado SSL/TLS</CardTitle></CardHeader>
                  <CardContent className="text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">Emissor</span><span>{intel.ssl_issuer || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Validade</span><span>{intel.ssl_valid_until || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Dias restantes</span><span>{intel.ssl_days_left ?? '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Protocolo</span><span>{intel.ssl_protocol || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Nome compatível</span>
                      <span>{intel.ssl_name_match === false ? 'Incompatível' : intel.ssl_name_match ? 'Sim' : '—'}</span></div>
                  </CardContent>
                </Card>
                <Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" /> Registro e proteção</CardTitle></CardHeader>
                  <CardContent className="text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">Registrar</span><span>{intel.domain_registrar || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Titular</span><span className="truncate max-w-[180px]">{intel.domain_registrant || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Expiração</span><span>{intel.domain_expires || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Criado em</span><span>{intel.domain_created || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Nameservers</span><span className="truncate max-w-[180px]">{(intel.domain_nameservers || []).join(', ') || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Servidor</span><span>{intel.server_header || '—'}</span></div>
                    <div className="flex justify-between items-center"><span className="text-muted-foreground">WAF/CDN</span>
                      <span className="flex gap-1">{(intel.waf_cdn || []).map(w => <Badge key={w} variant="secondary" className="text-xs">{w}</Badge>)}</span></div>
                  </CardContent>
                </Card>
              </div>

              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Apontamentos DNS</CardTitle></CardHeader>
                <CardContent className="text-sm grid md:grid-cols-4 gap-3">
                  {['A', 'CNAME', 'MX', 'NS'].map(t => (
                    <div key={t}>
                      <div className="text-xs uppercase text-muted-foreground tracking-wide">{t}</div>
                      {((intel.dns || {})[t] || []).map(v => <div key={v} className="font-mono text-xs truncate">{v}</div>)}
                      {((intel.dns || {})[t] || []).length === 0 && <div className="text-xs">—</div>}
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card><CardHeader className="pb-2">
                <CardTitle className="text-sm">Subdomínios descobertos ({intel.subdomains_total || 0})</CardTitle>
              </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader><TableRow><TableHead>Ativo</TableHead><TableHead>Apontamentos</TableHead><TableHead className="text-right">Ação</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {(intel.subdomains || []).map(s => (
                        <TableRow key={s.name}>
                          <TableCell className="font-mono text-xs">{s.name}</TableCell>
                          <TableCell className="text-xs">
                            {((s.records || {}).A || []).join(', ') || ((s.records || {}).CNAME || []).join(', ') || '—'}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="outline" onClick={() => promote(s.name)}
                              data-testid={`promote-${s.name}`}>
                              <ArrowUpRight className="h-3.5 w-3.5 mr-1" /> Monitorar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {(intel.subdomains || []).length === 0 && (
                        <TableRow><TableCell colSpan={3} className="text-center py-6 text-sm text-muted-foreground">
                          Nenhum subdomínio encontrado nos logs públicos de transparência de certificados
                        </TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Histórico de mudanças</CardTitle></CardHeader>
                <CardContent className="text-sm space-y-2">
                  {(detail.events || []).map(e => (
                    <div key={e.id} className="flex justify-between border-b pb-1">
                      <span>{e.field}</span>
                      <span className="text-muted-foreground text-xs">{e.from} → {e.to}</span>
                    </div>
                  ))}
                  {(detail.events || []).length === 0 && <p className="text-muted-foreground text-sm">Nenhuma mudança registrada</p>}
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Monitor history */}
      <Dialog open={!!history} onOpenChange={() => setHistory(null)}>
        <DialogContent className="sm:max-w-2xl flex flex-col max-h-[85vh]">
          <DialogHeader><DialogTitle>{history?.monitor?.name}</DialogTitle>
            <DialogDescription>
              Uptime {history?.uptime ?? '—'}% · latência média {history?.avg_latency_ms ?? '—'} ms
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1">
            <Table>
              <TableHeader><TableRow><TableHead>Momento</TableHead><TableHead>Status</TableHead><TableHead>Latência</TableHead><TableHead>Mensagem</TableHead></TableRow></TableHeader>
              <TableBody>
                {(history?.checks || []).slice(-100).reverse().map(c => (
                  <TableRow key={c.id}>
                    <TableCell className="text-sm">{new Date(c.created_at).toLocaleString('pt-BR')}</TableCell>
                    <TableCell>{statusDot(c.status)}</TableCell>
                    <TableCell className="text-sm">{c.latency_ms ? `${c.latency_ms} ms` : '—'}</TableCell>
                    <TableCell className="text-sm">{c.message}</TableCell>
                  </TableRow>
                ))}
                {(history?.checks || []).length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center py-6 text-sm text-muted-foreground">
                    Sem verificações registradas ainda
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
